/**
 * SyriaPHR API client — thin typed wrapper over the FastAPI backend.
 *
 * Base URL resolution:
 *   - `VITE_API_BASE_URL` env var wins (e.g. a deployed backend).
 *   - Otherwise, in the browser we default to the same origin's `/api/v1`
 *     (Cloudflare Pages Functions proxy in prod, or a dev proxy).
 *   - In tests / non-browser we fall back to the local FastAPI dev server.
 *
 * Endpoints used:
 *   - POST /api/v1/patients/register
 *   - POST /api/v1/patients/search
 *   - GET  /api/v1/patients/{id}
 *   - GET  /api/v1/terms/search?system=&q=&limit=
 */

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  (typeof window !== 'undefined'
    ? `${window.location.origin}/api/v1`
    : 'http://127.0.0.1:8000/api/v1')

export class ApiError extends Error {
  status: number
  detail: unknown
  constructor(status: number, detail: unknown) {
    super(typeof detail === 'string' ? detail : `Request failed (${status})`)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (!res.ok) {
    let detail: unknown = res.statusText
    try {
      const body = await res.json()
      detail = body.detail ?? body
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/* ============================================================
   Patients
   ============================================================ */

export interface PersonResponse {
  id: string
  nhi: string
  name_ar?: string | null
  name_latin?: string | null
  dob?: string | null
  sex?: string | null
  phone?: string | null
}

export interface PatientResponse {
  id: string
  person_id: string
  facility_id: string
  mrn: string
  verification_tier: number
  status: string
  person: PersonResponse
}

export interface MatchCandidate {
  person_id: string
  patient_id?: string | null
  nhi: string
  name_ar?: string | null
  name_latin?: string | null
  dob?: string | null
  score: number
  match_reason: string
}

export interface PatientSearchResponse {
  candidates: MatchCandidate[]
  search_time_ms: number
}

export interface RegistrationResponse {
  patient: PatientResponse
  mpi_match?: MatchCandidate | null
  action: string // "created_new" | "linked_existing"
}

export interface PatientRegistrationPayload {
  moi_id?: string
  name_ar?: string
  name_latin?: string
  dob?: string
  sex?: 'M' | 'F'
  phone?: string
  address?: Record<string, string>
  facility?: {
    facility_id: string
    mrn: string
    verification_tier?: number
    registration_source?: string
  }
}

export interface PatientSearchPayload {
  moi_id?: string
  name_ar?: string
  name_latin?: string
  dob?: string
  phone?: string
  sex?: 'M' | 'F'
}

export const patientsApi = {
  register: (payload: PatientRegistrationPayload) =>
    request<RegistrationResponse>('/patients/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  search: (payload: PatientSearchPayload) =>
    request<PatientSearchResponse>('/patients/search', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  get: (id: string) => request<PatientResponse>(`/patients/${id}`),
}

/* ============================================================
   Terminology
   ============================================================ */

export interface TermResult {
  system: string
  code: string
  name: string
  extra: Record<string, unknown>
}

export interface TermSearchResponse {
  results: TermResult[]
  took_ms: number
  count: number
}

export const termsApi = {
  search: (system: string, q: string, limit = 20) => {
    const params = new URLSearchParams({ system, q, limit: String(limit) })
    return request<TermSearchResponse>(`/terms/search?${params.toString()}`)
  },
}
